// Haptic feedback utilities for mobile devices
// Provides subtle tactile feedback for key interactions

export type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const HAPTIC_PATTERNS: Record<HapticType, number[]> = {
  light: [10],
  medium: [20],
  heavy: [30],
  success: [10, 10, 10],
  warning: [20, 10, 20],
  error: [30, 20, 30],
};

/**
 * Triggers haptic feedback on supported devices
 * @param type - The type of haptic feedback to trigger
 * @param fallback - Whether to use a fallback for unsupported devices (default: true)
 */
export function triggerHaptic(type: HapticType = 'light', fallback: boolean = true): void {
  try {
    if (navigator.vibrate && typeof navigator.vibrate === 'function') {
      navigator.vibrate(HAPTIC_PATTERNS[type]);
    } else if (fallback && 'vibrate' in navigator) {
      // Fallback for some browsers that support vibrate but not the pattern
      navigator.vibrate(10);
    }
  } catch (error) {
    // Silently fail - haptics are a nice-to-have, not essential
    console.debug('Haptic feedback not available:', error);
  }
}

/**
 * Check if haptic feedback is supported on the current device
 */
export function isHapticSupported(): boolean {
  try {
    return 'vibrate' in navigator && typeof navigator.vibrate === 'function';
  } catch {
    return false;
  }
}

/**
 * Hook for React components to easily add haptic feedback
 */
export function useHaptics() {
  return {
    light: () => triggerHaptic('light'),
    medium: () => triggerHaptic('medium'),
    heavy: () => triggerHaptic('heavy'),
    success: () => triggerHaptic('success'),
    warning: () => triggerHaptic('warning'),
    error: () => triggerHaptic('error'),
    isSupported: isHapticSupported(),
  };
}